import { useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Wind, Thermometer, Droplets, Gauge, CheckCircle2, AlertCircle,
  Info, RotateCcw, FileText, TrendingUp, Zap, FlaskConical,
  ChevronDown, ChevronUp, X, Beaker, BarChart3, Settings,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { ScienceAccordion } from '@/components/ScienceAccordion';
import type { ScienceSection } from '@/components/ScienceAccordion';
import {
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';

/* ─── Packed-Bed Air Stripping Column — General Purpose Calculator ─────────
   Countercurrent packed-column NH3/NH4+ removal.
   Standard model valid 5–40°C; linear HTU temperature correction.
   For high-temperature (>50°C) or VTA/venturi-type contactors, use the
   VTA Hot Stripper tool instead.
   ────────────────────────────────────────────────────────────────────────── */

// ── Types ──────────────────────────────────────────────────────────────────

type PackingType = 'pall' | 'raschig' | 'structured' | 'saddles';
type ReagentType = 'NaOH' | 'lime' | 'none';

interface StripperParams {
  flowRate:      number;   // m³/h
  inletTAN:      number;   // mg/L
  targetTAN:     number;   // mg/L
  pH:            number;
  temperature:   number;   // °C
  packingType:   PackingType;
  glRatio:       number;   // G/L volumetric air-to-water ratio
  liquidLoading: number;   // m³/m²·h
  safetyFactor:  number;
  reagent:       ReagentType;
}

interface StripperResult {
  pKa:     number;
  fNH3:    number;
  freeNH3: number;
  nh4conc: number;
  hcc:     number;
  hEff:    number;
  S:       number;
  NTU:     number;
  HTU:     number;
  Z:       number;
  Zdesign: number;
  A:       number;
  D:       number;
  Qair:    number;
  staticHead:      number;
  blowerPressure:  number;
  blowerPower:     number;
  tanOut:   number;
  removal:  number;
  pHDose:   number;
  doIncrease: number;
  validations: { label: string; pass: boolean; value: string }[];
}

// ── Constants ───────────────────────────────────────────────────────────────

const PACKING_HTU20: Record<PackingType, { htu: number; label: string }> = {
  pall:       { htu: 1.1, label: 'Plastic Pall rings 50 mm' },
  raschig:    { htu: 1.4, label: 'Plastic Raschig rings 50 mm' },
  structured: { htu: 0.7, label: 'Structured packing (Mellapak 250Y)' },
  saddles:    { htu: 0.9, label: 'Plastic saddles 38 mm' },
};

const REAGENT_LABELS: Record<ReagentType, string> = {
  NaOH: 'NaOH (Caustic soda)',
  lime: 'Ca(OH)₂ (Lime)',
  none: 'None (no pH correction)',
};

const DEFAULT_PARAMS: StripperParams = {
  flowRate:      100,
  inletTAN:      50,
  targetTAN:     5,
  pH:            10.5,
  temperature:   15,
  packingType:   'pall',
  glRatio:       200,
  liquidLoading: 15,
  safetyFactor:  1.2,
  reagent:       'NaOH',
};

// ── Core Calculation ────────────────────────────────────────────────────────

function calcStripper(p: StripperParams): StripperResult {
  const T_K = p.temperature + 273.15;

  // 1. NH3/NH4+ equilibrium (Henderson–Hasselbalch)
  const pKa   = 0.09018 + 2729.92 / T_K;
  const fNH3  = 1 / (1 + Math.pow(10, pKa - p.pH));
  const freeNH3 = p.inletTAN * fNH3;
  const nh4conc = p.inletTAN * (1 - fNH3);

  // 2. Henry's constant (Staudinger & Roberts, 2001)
  const hcc  = 0.01144 * Math.exp(4092 * (1 / 293.15 - 1 / T_K));
  const hEff = hcc * fNH3;

  // 3. Stripping factor
  const S = hEff * p.glRatio;

  // 4. NTU (countercurrent packed tower, Strigle 1994)
  let NTU = 0;
  if (S > 1.001 && p.inletTAN > 0 && p.targetTAN > 0 && p.inletTAN > p.targetTAN) {
    const ratio = p.inletTAN / p.targetTAN;
    NTU = (S / (S - 1)) * Math.log(ratio * (1 - 1 / S) + 1 / S);
  } else if (S <= 1.001 && p.inletTAN > 0 && p.targetTAN > 0) {
    NTU = Math.log(p.inletTAN / p.targetTAN);
  }
  NTU = Math.max(0, NTU);

  // 5. HTU — linear temperature correction (valid 5–40°C)
  //    HTU(T) = HTU₂₀ × (1.0 − 0.015 × (T − 20))
  //    Note: becomes unreliable above ~40°C. Use VTA Hot Stripper for T > 50°C.
  const htu20 = PACKING_HTU20[p.packingType].htu;
  const HTU   = Math.max(0.1, htu20 * (1.0 - 0.015 * (p.temperature - 20)));

  // 6. Tower geometry
  const Z       = NTU * HTU;
  const Zdesign = Math.max(Z * p.safetyFactor, 2.0);
  const A       = p.flowRate / p.liquidLoading;
  const D       = Math.sqrt((4 * A) / Math.PI);

  // 7. Air blower
  const Qair           = p.flowRate * p.glRatio;
  const staticHead     = Zdesign * 0.2;               // kPa (0.2 kPa/m packing)
  const blowerPressure = staticHead + 0.5;             // + inlet/outlet losses
  const blowerPower    = (Qair * blowerPressure) / (3600 * 0.70);  // kW (η=70%)

  // 8. Effluent TAN back-calculation from actual design NTU
  const ntuActual = Zdesign / HTU;
  let tanOutActual = p.targetTAN;
  if (S > 1.001 && ntuActual > 0) {
    const x  = ((S - 1) / S) * ntuActual;
    const ex = Math.exp(x);
    tanOutActual = Math.max(0, (p.inletTAN * (1 - 1 / S)) / (ex - 1 / S));
  } else if (S <= 1.001 && ntuActual > 0) {
    tanOutActual = p.inletTAN * Math.exp(-ntuActual);
  }
  const removal = p.inletTAN > 0
    ? Math.min(100, ((p.inletTAN - tanOutActual) / p.inletTAN) * 100)
    : 0;

  // 9. pH dose estimate
  let pHDose = 0;
  if (p.reagent !== 'none') {
    pHDose = p.pH >= 10.0
      ? (p.reagent === 'NaOH' ? 120 : 180)
      : Math.max(50, (p.pH - 7.5) * (p.reagent === 'NaOH' ? 40 : 74) * 2);
  }

  // 10. DO increase (re-aeration)
  const doIncrease = Math.min(8, 0.005 * p.glRatio);

  // 11. Validations
  const validations: StripperResult['validations'] = [
    { label: 'pH ≥ 10.0 for effective stripping',         pass: p.pH >= 10.0,              value: `pH = ${p.pH.toFixed(1)}` },
    { label: 'Stripping factor S ≥ 1.5',                  pass: S >= 1.5,                  value: `S = ${S.toFixed(2)}` },
    { label: 'NTU between 2 and 20 (feasible tower)',     pass: NTU >= 2 && NTU <= 20,     value: `NTU = ${NTU.toFixed(2)}` },
    { label: 'Tower design height ≤ 15 m',                pass: Zdesign <= 15,             value: `Z = ${Zdesign.toFixed(1)} m` },
    { label: 'Liquid loading ≤ 40 m³/m²·h (flood limit)', pass: p.liquidLoading <= 40,     value: `QL = ${p.liquidLoading.toFixed(0)} m³/m²·h` },
    { label: 'Removal efficiency ≥ 90%',                  pass: removal >= 90,             value: `η = ${removal.toFixed(1)}%` },
    { label: 'G/L ratio ≤ 500 (blower energy limit)',      pass: p.glRatio <= 500,          value: `G/L = ${p.glRatio}` },
    { label: 'Temperature in linear-HTU valid range (≤ 40°C)', pass: p.temperature <= 40,  value: `T = ${p.temperature}°C` },
  ];

  return {
    pKa, fNH3, freeNH3, nh4conc,
    hcc, hEff, S, NTU, HTU,
    Z, Zdesign, A, D,
    Qair, staticHead, blowerPressure, blowerPower,
    tanOut: tanOutActual,
    removal, pHDose, doIncrease,
    validations,
  };
}

// ── Format helper ────────────────────────────────────────────────────────────

const f = (v: number, d = 2) => isFinite(v) && !isNaN(v) ? v.toFixed(d) : '—';

// ── Input Slider Row ─────────────────────────────────────────────────────────

function SliderRow({
  label, value, min, max, step = 1, unit, onChange, warning,
}: {
  label: string; value: number; min: number; max: number;
  step?: number; unit: string; onChange: (v: number) => void; warning?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-slate-300">{label}</label>
        <div className="flex items-center gap-1.5">
          <input
            type="number" value={value} min={min} max={max} step={step}
            onChange={e => onChange(parseFloat(e.target.value) || 0)}
            className="w-20 text-right text-xs px-2 py-0.5 text-slate-100 focus:outline-none focus:border-sky-500 rb-inner-card-sm"
          />
          <span className="text-[11px] text-slate-500 w-14">{unit}</span>
        </div>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 accent-sky-500 cursor-pointer"
      />
      {warning && (
        <p className="text-[11px] text-amber-400 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {warning}
        </p>
      )}
    </div>
  );
}

// ── Metric Card ──────────────────────────────────────────────────────────────

function MetricCard({
  label, value, unit, sub, accent = false, warn = false,
}: {
  label: string; value: string; unit?: string; sub?: string; accent?: boolean; warn?: boolean;
}) {
  return (
    <div className={`rounded-xl p-3 border ${
      warn   ? 'bg-amber-500/5 border-amber-500/30'
      : accent ? 'bg-sky-500/5 border-sky-500/20'
      :          'bg-rb-surface border-slate-800'
    }`}>
      <div className="text-[11px] text-slate-400 mb-1">{label}</div>
      <div className={`text-lg font-bold ${warn ? 'text-amber-300' : accent ? 'text-sky-300' : 'text-slate-100'}`}>
        {value}
        {unit && <span className="text-xs font-normal text-slate-400 ml-1">{unit}</span>}
      </div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ── Report Modal ─────────────────────────────────────────────────────────────

function ReportModal({ params, result, onClose }: { params: StripperParams; result: StripperResult; onClose: () => void }) {
  const packLabel = PACKING_HTU20[params.packingType].label;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#0a0f1a] border border-slate-700 shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#0a0f1a]">
          <div>
            <h2 className="text-base font-bold text-slate-100">Packed-Bed Air Stripping Column — Validation Report</h2>
            <p className="text-xs text-slate-500 mt-0.5">Reynolds Bauhm Smart Water | {new Date().toLocaleDateString('en-GB')}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        <div className="p-6 space-y-6 text-sm">
          <section>
            <h3 className="font-bold text-sky-400 text-xs uppercase tracking-widest mb-3">Input Parameters</h3>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[
                ['Flow rate', `${params.flowRate} m³/h`],
                ['Inlet TAN', `${params.inletTAN} mg/L`],
                ['Target effluent TAN', `${params.targetTAN} mg/L`],
                ['pH', params.pH.toString()],
                ['Temperature', `${params.temperature} °C`],
                ['Packing type', packLabel],
                ['G/L ratio', params.glRatio.toString()],
                ['Liquid loading', `${params.liquidLoading} m³/m²·h`],
                ['Safety factor', params.safetyFactor.toString()],
                ['pH reagent', REAGENT_LABELS[params.reagent]],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between bg-rb-surface rounded px-3 py-1.5 border border-slate-800">
                  <span className="text-slate-400">{k}</span>
                  <span className="text-slate-100 font-medium">{v}</span>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h3 className="font-bold text-sky-400 text-xs uppercase tracking-widest mb-3">Calculation Breakdown</h3>
            <div className="space-y-2 text-xs font-mono">
              {[
                ['pKa(T)',      `0.09018 + 2729.92 / ${f(params.temperature+273.15,2)} = ${f(result.pKa,4)}`],
                ['f_NH₃',       `1 / (1 + 10^(${f(result.pKa,4)} − ${params.pH})) = ${f(result.fNH3,4)} (${f(result.fNH3*100,1)}%)`],
                ['Free NH₃',   `${params.inletTAN} × ${f(result.fNH3,4)} = ${f(result.freeNH3,2)} mg/L`],
                ['NH₄⁺',        `${params.inletTAN} × ${f(1-result.fNH3,4)} = ${f(result.nh4conc,2)} mg/L`],
                ['Hcc(T)',      `0.01144 × exp(4092 × (1/293.15 − 1/${f(params.temperature+273.15,2)})) = ${f(result.hcc,5)}`],
                ['H_eff',       `${f(result.hcc,5)} × ${f(result.fNH3,4)} = ${f(result.hEff,5)}`],
                ['S',           `${f(result.hEff,5)} × ${params.glRatio} = ${f(result.S,3)}`],
                ['NTU',         `(S/(S−1)) × ln[(Cin/Cout)×(1−1/S)+1/S] = ${f(result.NTU,3)}`],
                ['HTU(T)',      `${PACKING_HTU20[params.packingType].htu} × (1 − 0.015 × (${params.temperature}−20)) = ${f(result.HTU,3)} m`],
                ['Z',           `${f(result.NTU,3)} × ${f(result.HTU,3)} = ${f(result.Z,2)} m`],
                ['Z_design',    `max(${f(result.Z,2)} × ${params.safetyFactor}, 2.0) = ${f(result.Zdesign,2)} m`],
                ['A',           `${params.flowRate} / ${params.liquidLoading} = ${f(result.A,2)} m²`],
                ['D',           `√(4 × ${f(result.A,2)} / π) = ${f(result.D,2)} m`],
                ['Q_air',       `${params.flowRate} × ${params.glRatio} = ${f(result.Qair,0)} m³/h`],
                ['ΔP_blower',   `${f(result.staticHead,2)} + 0.5 = ${f(result.blowerPressure,2)} kPa`],
                ['Power',       `${f(result.Qair,0)} × ${f(result.blowerPressure,2)} / (3600 × 0.7) = ${f(result.blowerPower,2)} kW`],
                ['TAN out',     `${f(result.tanOut,2)} mg/L  (η = ${f(result.removal,1)}%)`],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-5 gap-2 bg-rb-surface rounded px-3 py-1.5 border border-slate-800">
                  <span className="text-slate-400 col-span-2">{k}</span>
                  <span className="text-sky-300 col-span-3">{v}</span>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h3 className="font-bold text-sky-400 text-xs uppercase tracking-widest mb-3">Validation Summary</h3>
            <div className="space-y-1.5">
              {result.validations.map((v, i) => (
                <div key={i} className={`flex items-center justify-between rounded-lg px-3 py-2 text-xs border ${
                  v.pass ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-rose-500/5 border-rose-500/20'
                }`}>
                  <div className="flex items-center gap-2">
                    {v.pass ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <AlertCircle className="w-3.5 h-3.5 text-rose-400" />}
                    <span className={v.pass ? 'text-slate-200' : 'text-slate-300'}>{v.label}</span>
                  </div>
                  <span className={`font-mono font-bold ${v.pass ? 'text-emerald-400' : 'text-rose-400'}`}>{v.value}</span>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h3 className="font-bold text-sky-400 text-xs uppercase tracking-widest mb-3">Temperature Sensitivity</h3>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-700">
                  {['T (°C)', 'Hcc', 'S', 'NTU', 'HTU (m)', 'Z_design (m)', 'η (%)'].map(h => (
                    <th key={h} className="text-left py-1.5 px-2 text-slate-400 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[params.temperature - 5, params.temperature, params.temperature + 5, params.temperature + 10]
                  .filter(t => t >= 5 && t <= 40)
                  .map(t => {
                    const r = calcStripper({ ...params, temperature: t });
                    const isCurrent = t === params.temperature;
                    return (
                      <tr key={t} className={`border-b border-slate-800 ${isCurrent ? 'bg-sky-500/5' : ''}`}>
                        <td className={`py-1.5 px-2 font-mono ${isCurrent ? 'text-sky-300 font-bold' : 'text-slate-300'}`}>{t}</td>
                        <td className="py-1.5 px-2 font-mono text-slate-300">{f(r.hcc,4)}</td>
                        <td className="py-1.5 px-2 font-mono text-slate-300">{f(r.S,2)}</td>
                        <td className="py-1.5 px-2 font-mono text-slate-300">{f(r.NTU,2)}</td>
                        <td className="py-1.5 px-2 font-mono text-slate-300">{f(r.HTU,3)}</td>
                        <td className="py-1.5 px-2 font-mono text-slate-300">{f(r.Zdesign,2)}</td>
                        <td className="py-1.5 px-2 font-mono text-slate-300">{f(r.removal,1)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </section>
          <section>
            <h3 className="font-bold text-sky-400 text-xs uppercase tracking-widest mb-2">References</h3>
            <ul className="text-xs text-slate-500 space-y-1 list-disc list-inside">
              <li>Metcalf & Eddy, <em>Wastewater Engineering: Treatment and Resource Recovery</em>, 5th Ed. (2014)</li>
              <li>WEF MOP 8 — <em>Design of Municipal Wastewater Treatment Plants</em></li>
              <li>US EPA Design Manual — Ammonia Stripping (EPA/625/1-74/016)</li>
              <li>Staudinger & Roberts, <em>Chemosphere</em> 44 (2001) — Henry's law constants</li>
              <li>Strigle, <em>Packed Tower Design and Applications</em>, 2nd Ed. (1994)</li>
            </ul>
          </section>
        </div>
        <div className="px-6 pb-6 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-rb-brand text-white text-sm font-semibold hover:bg-rb-brand/90 transition-colors">
            Close Report
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function PackedColumnStripperPage() {
  const [params, setParams]         = useState<StripperParams>(DEFAULT_PARAMS);
  const [showReport, setShowReport] = useState(false);
  const [sciOpen, setSciOpen]       = useState(false);

  const set = useCallback(<K extends keyof StripperParams>(key: K, value: StripperParams[K]) => {
    setParams(prev => ({ ...prev, [key]: value }));
  }, []);

  const result = useMemo(() => calcStripper(params), [params]);

  const handleReset = () => { setParams(DEFAULT_PARAMS); toast.success('Parameters reset to defaults'); };

  // ── Chart data ───────────────────────────────────────────────────────────
  const phChartData = useMemo(() =>
    Array.from({ length: 25 }, (_, i) => {
      const pH = 6 + i * 0.25;
      const r = calcStripper({ ...params, pH });
      return { pH: parseFloat(pH.toFixed(2)), removal: parseFloat(r.removal.toFixed(1)) };
    }), [params]);

  const tempChartData = useMemo(() =>
    Array.from({ length: 8 }, (_, i) => {
      const temp = 5 + i * 5;
      const r = calcStripper({ ...params, temperature: temp });
      return { temp, Zdesign: parseFloat(r.Zdesign.toFixed(2)), removal: parseFloat(r.removal.toFixed(1)) };
    }), [params]);

  // Must be declared before scienceSections useMemo (TDZ guard)
  const htu20 = PACKING_HTU20[params.packingType].htu;

  // ── Science accordion sections ───────────────────────────────────────────
  const scienceSections = useMemo((): ScienceSection[] => {
    const T_K = params.temperature + 273.15;
    const r = result;
    return [
      {
        title: 'Step 1 — NH₃/NH₄⁺ Equilibrium (Temperature-Corrected)',
        icon: FlaskConical,
        equations: [
          {
            label: 'pKa at temperature T (Bates & Pinching, 1949)',
            formula: 'pKa(T) = 0.09018 + 2729.92 / T_K',
            substitution: `pKa(${params.temperature}°C) = 0.09018 + 2729.92 / ${f(T_K,2)} = ${f(r.pKa,4)}`,
            result: f(r.pKa,4),
            status: 'pass',
          },
          {
            label: 'Free ammonia fraction f_NH₃',
            formula: 'f_NH₃ = 1 / (1 + 10^(pKa − pH))',
            substitution: `f_NH₃ = 1 / (1 + 10^(${f(r.pKa,4)} − ${params.pH})) = ${f(r.fNH3,4)} (${f(r.fNH3*100,1)}%)`,
            result: `${f(r.fNH3*100,1)}%`,
            status: r.fNH3 > 0.5 ? 'pass' : r.fNH3 > 0.2 ? 'warn' : 'fail',
            unit: 'fraction of TAN as volatile NH₃',
          },
          {
            label: 'Free NH₃ concentration (volatile)',
            formula: 'Free NH₃ = TAN × f_NH₃',
            substitution: `Free NH₃ = ${params.inletTAN} × ${f(r.fNH3,4)} = ${f(r.freeNH3,2)} mg/L`,
            result: `${f(r.freeNH3,2)} mg/L`,
            status: 'pass',
          },
          {
            label: 'NH₄⁺ concentration (ionic, non-volatile)',
            formula: 'NH₄⁺ = TAN × (1 − f_NH₃)',
            substitution: `NH₄⁺ = ${params.inletTAN} × ${f(1-r.fNH3,4)} = ${f(r.nh4conc,2)} mg/L`,
            result: `${f(r.nh4conc,2)} mg/L`,
            status: 'pass',
            unit: '(remains in effluent — not strippable)',
          },
        ],
        warnings: params.pH < 10 ? [`pH ${params.pH} gives only ${f(r.fNH3*100,1)}% free NH₃. Raise to ≥10.0 for effective stripping.`] : undefined,
      },
      {
        title: "Step 2 — Henry's Law Constants",
        icon: Beaker,
        equations: [
          {
            label: "Dimensionless Henry's constant Hcc (Staudinger & Roberts, 2001)",
            formula: 'Hcc(T) = 0.01144 × exp[4092 × (1/293.15 − 1/T_K)]',
            substitution: `Hcc(${params.temperature}°C) = 0.01144 × exp(${f(4092*(1/293.15-1/T_K),4)}) = ${f(r.hcc,5)}`,
            result: f(r.hcc,5),
            status: 'pass',
            unit: 'mol_gas / mol_liq',
          },
          {
            label: 'Effective Henry constant (only free NH₃ is volatile)',
            formula: 'H_eff = Hcc × f_NH₃',
            substitution: `H_eff = ${f(r.hcc,5)} × ${f(r.fNH3,4)} = ${f(r.hEff,5)}`,
            result: f(r.hEff,5),
            status: r.hEff > 0.001 ? 'pass' : 'warn',
          },
        ],
      },
      {
        title: 'Step 3 — Stripping Factor & NTU',
        icon: TrendingUp,
        equations: [
          {
            label: 'Stripping factor S = H_eff × (G/L)',
            formula: 'S = H_eff × G/L   (S > 1 required for net transfer to gas phase)',
            substitution: `S = ${f(r.hEff,5)} × ${params.glRatio} = ${f(r.S,3)}`,
            result: f(r.S,3),
            status: r.S >= 5 ? 'pass' : r.S >= 1.5 ? 'warn' : 'fail',
            unit: r.S >= 5 ? '(good efficiency)' : r.S >= 1.5 ? '(marginal)' : '(POOR — increase G/L or pH)',
          },
          {
            label: 'Number of Transfer Units (countercurrent, Strigle 1994)',
            formula: 'NTU = [S/(S−1)] × ln[(Cin/Cout)×(1−1/S) + 1/S]',
            substitution: `NTU = [${f(r.S,3)}/${f(r.S-1,3)}] × ln[…] = ${f(r.NTU,3)}`,
            result: f(r.NTU,3),
            status: r.NTU >= 2 && r.NTU <= 20 ? 'pass' : 'warn',
          },
        ],
        warnings: r.S < 1.5 ? [`S = ${f(r.S,2)} < 1.5. Increase G/L ratio or raise pH to improve volatilisation.`] : undefined,
      },
      {
        title: 'Step 4 — Tower Geometry (HTU, Height, Diameter)',
        icon: Gauge,
        equations: [
          {
            label: 'HTU at operating temperature (linear correction, valid 5–40°C)',
            formula: 'HTU(T) = HTU₂₀ × (1.0 − 0.015 × (T − 20))',
            substitution: `HTU = ${htu20} × (1.0 − 0.015 × (${params.temperature} − 20)) = ${f(r.HTU,3)} m`,
            result: `${f(r.HTU,3)} m`,
            status: params.temperature <= 40 ? 'pass' : 'fail',
            unit: `HTU₂₀ = ${htu20} m (${PACKING_HTU20[params.packingType].label})`,
          },
          {
            label: 'Theoretical tower height Z = NTU × HTU',
            formula: 'Z = NTU × HTU(T)',
            substitution: `Z = ${f(r.NTU,3)} × ${f(r.HTU,3)} = ${f(r.Z,2)} m`,
            result: `${f(r.Z,2)} m`,
            status: 'pass',
          },
          {
            label: 'Design height (with safety factor, min 2.0 m structural)',
            formula: 'Z_design = max(Z × SF, 2.0)',
            substitution: `Z_design = max(${f(r.Z,2)} × ${params.safetyFactor}, 2.0) = ${f(r.Zdesign,2)} m`,
            result: `${f(r.Zdesign,2)} m`,
            status: r.Zdesign <= 15 ? 'pass' : r.Zdesign <= 20 ? 'warn' : 'fail',
          },
          {
            label: 'Cross-section A = Q / QL',
            formula: 'A = Q_water / liquid_loading',
            substitution: `A = ${params.flowRate} / ${params.liquidLoading} = ${f(r.A,2)} m²`,
            result: `${f(r.A,2)} m²`,
            status: 'pass',
          },
          {
            label: 'Tower diameter D = √(4A/π)',
            formula: 'D = √(4 × A / π)',
            substitution: `D = √(4 × ${f(r.A,2)} / π) = ${f(r.D,2)} m`,
            result: `${f(r.D,2)} m`,
            status: 'pass',
          },
        ],
        warnings: r.Zdesign > 15 ? [`Design height ${f(r.Zdesign,1)} m exceeds 15 m practical limit. Consider two towers in series or increase G/L.`] : undefined,
      },
      {
        title: 'Step 5 — Air Blower Sizing',
        icon: Wind,
        equations: [
          {
            label: 'Air flow rate Q_air = Q_water × G/L',
            formula: 'Q_air = Q_water × G/L',
            substitution: `Q_air = ${params.flowRate} × ${params.glRatio} = ${f(r.Qair,0)} m³/h`,
            result: `${f(r.Qair,0)} m³/h`,
            status: 'pass',
          },
          {
            label: 'Packing pressure drop (0.2 kPa/m typical random packing)',
            formula: 'ΔP_packing = 0.2 kPa/m × Z_design',
            substitution: `ΔP = 0.2 × ${f(r.Zdesign,2)} = ${f(r.staticHead,2)} kPa`,
            result: `${f(r.staticHead,2)} kPa`,
            status: 'pass',
          },
          {
            label: 'Total blower pressure (+ inlet/outlet losses)',
            formula: 'ΔP_total = ΔP_packing + 0.5 kPa',
            substitution: `ΔP_total = ${f(r.staticHead,2)} + 0.5 = ${f(r.blowerPressure,2)} kPa`,
            result: `${f(r.blowerPressure,2)} kPa`,
            status: 'pass',
          },
          {
            label: 'Blower shaft power (η = 70%)',
            formula: 'P = Q_air × ΔP / (3600 × 0.70)  kW',
            substitution: `P = ${f(r.Qair,0)} × ${f(r.blowerPressure,2)} / 2520 = ${f(r.blowerPower,2)} kW`,
            result: `${f(r.blowerPower,2)} kW`,
            status: 'pass',
          },
        ],
      },
      {
        title: 'Step 6 — Effluent Quality & Removal',
        icon: Droplets,
        equations: [
          {
            label: 'Estimated effluent TAN (back-calculated from Z_design/HTU)',
            formula: 'C_out = C_in × (1−1/S) / (e^x − 1/S)  where x = (S−1)/S × NTU_actual',
            substitution: `C_out = ${f(r.tanOut,2)} mg/L (N_actual = ${f(r.Zdesign/r.HTU,2)} NTU)`,
            result: `${f(r.tanOut,2)} mg/L`,
            status: r.tanOut <= params.targetTAN * 1.05 ? 'pass' : 'warn',
          },
          {
            label: 'Removal efficiency η',
            formula: 'η = (Cin − Cout) / Cin × 100 %',
            substitution: `η = (${params.inletTAN} − ${f(r.tanOut,2)}) / ${params.inletTAN} × 100 = ${f(r.removal,1)}%`,
            result: `${f(r.removal,1)}%`,
            status: r.removal >= 90 ? 'pass' : r.removal >= 75 ? 'warn' : 'fail',
          },
          {
            label: 'DO increase (re-aeration, empirical)',
            formula: 'ΔDO ≈ 0.005 × G/L  mg/L',
            substitution: `ΔDO ≈ 0.005 × ${params.glRatio} = ${f(r.doIncrease,2)} mg/L`,
            result: `+${f(r.doIncrease,2)} mg/L`,
            status: 'pass',
            unit: 'DO increase (beneficial for downstream biology)',
          },
        ],
      },
    ];
  }, [params, result]);

  const passCount = result.validations.filter(v => v.pass).length;
  const failCount = result.validations.length - passCount;

  return (
    <div className="min-h-screen bg-rb-bg p-4 md:p-6 space-y-6">

      <PageHeader
        icon={Wind}
        title="Packed-Bed Air Stripping Column"
        subtitle="Countercurrent packed-column NH₃ removal — standard model for ambient/warm service (5–40°C)"
        iconColor="text-sky-400"
        actions={
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2 py-1 rounded-lg ${
              failCount === 0 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
              : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
            }`}>
              {passCount}/{result.validations.length} checks passed
            </span>
            <button onClick={handleReset} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-300 hover:bg-slate-800 transition-colors">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
            <button onClick={() => setShowReport(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rb-brand text-white text-xs font-semibold hover:bg-rb-brand/90 transition-colors">
              <FileText className="w-3.5 h-3.5" /> Report
            </button>
          </div>
        }
      />

      {/* Scope note */}
      <div className="flex items-start gap-3 bg-sky-500/5 border border-sky-500/20 rounded-xl px-4 py-3">
        <Info className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
        <p className="text-xs text-sky-300">
          <strong>Scope:</strong> Conventional packed-bed (random or structured packing) countercurrent air stripping,
          valid 5–40°C using linear HTU temperature correction. For hot stripping (80–90°C), VTA/venturi contactors,
          two-stage series cascades or high-load industrial systems, use the <strong>VTA Hot Stripper</strong> tool.
        </p>
      </div>

      {/* pH warning */}
      {params.pH < 10 && (
        <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
          <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-300">
            <strong>Low pH:</strong> At pH {params.pH.toFixed(1)}, only {f(result.fNH3*100,1)}% of TAN is free NH₃.
            Stripping will be highly inefficient. Raise to ≥ 10.0.
          </p>
        </div>
      )}

      {/* Temperature warning */}
      {params.temperature > 40 && (
        <div className="flex items-start gap-3 bg-orange-500/10 border border-orange-500/30 rounded-xl px-4 py-3">
          <AlertCircle className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
          <p className="text-xs text-orange-300">
            <strong>Temperature out of range ({params.temperature}°C):</strong> Linear HTU correction is invalid above ~40°C.
            Results will be inaccurate. Use the <strong>VTA Hot Stripper</strong> for high-temperature service.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[400px_1fr] gap-6">

        {/* ─ LEFT: Inputs ─ */}
        <div className="space-y-4">

          <div className="rb-card p-5 space-y-4 rb-section">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200">
              <Droplets className="w-4 h-4 text-sky-400" /> Water Quality
            </h2>
            <SliderRow label="Flow rate Q" value={params.flowRate} min={1} max={5000} step={1} unit="m³/h"
              onChange={v => set('flowRate', v)} />
            <SliderRow label="Inlet TAN" value={params.inletTAN} min={1} max={500} step={1} unit="mg/L"
              onChange={v => set('inletTAN', v)} />
            <SliderRow label="Target effluent TAN" value={params.targetTAN} min={0.1} max={50} step={0.1} unit="mg/L"
              onChange={v => set('targetTAN', Math.min(v, params.inletTAN - 0.1))} />
            <SliderRow label="pH" value={params.pH} min={7} max={12} step={0.1} unit="—"
              onChange={v => set('pH', v)}
              warning={params.pH < 10 ? 'pH < 10 — stripping becomes inefficient' : undefined} />
            <SliderRow label="Temperature" value={params.temperature} min={5} max={40} step={1} unit="°C"
              onChange={v => set('temperature', v)}
              warning={params.temperature < 10 ? 'Below 10°C — Hcc drops significantly' : params.temperature > 35 ? 'Approaching linear-HTU model limit (>40°C)' : undefined} />
          </div>

          <div className="rb-card p-5 space-y-4 rb-section">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200">
              <Settings className="w-4 h-4 text-sky-400" /> Tower Design
            </h2>
            <div>
              <label className="text-xs font-medium text-slate-300 block mb-1.5">Packing type</label>
              <select value={params.packingType} onChange={e => set('packingType', e.target.value as PackingType)}
                className="w-full text-sm px-3 py-2 text-slate-100 focus:outline-none focus:border-sky-500 rb-inner-card-sm">
                {(Object.entries(PACKING_HTU20) as [PackingType, { htu: number; label: string }][]).map(([k, v]) => (
                  <option key={k} value={k}>{v.label} — HTU₂₀ = {v.htu} m</option>
                ))}
              </select>
            </div>
            <SliderRow label="Air-to-water ratio G/L" value={params.glRatio} min={50} max={600} step={10} unit="—"
              onChange={v => set('glRatio', v)}
              warning={params.glRatio > 500 ? 'G/L > 500 — high blower energy cost' : undefined} />
            <SliderRow label="Liquid loading rate QL" value={params.liquidLoading} min={5} max={40} step={1} unit="m³/m²·h"
              onChange={v => set('liquidLoading', v)}
              warning={params.liquidLoading > 40 ? 'Exceeds flood limit (40 m³/m²·h)' : undefined} />
            <SliderRow label="Safety factor" value={params.safetyFactor} min={1.1} max={1.5} step={0.05} unit="—"
              onChange={v => set('safetyFactor', v)} />
          </div>

          <div className="rb-card p-5 space-y-4 rb-section">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200">
              <Beaker className="w-4 h-4 text-sky-400" /> pH Correction
            </h2>
            <div>
              <label className="text-xs font-medium text-slate-300 block mb-1.5">pH correction reagent</label>
              <select value={params.reagent} onChange={e => set('reagent', e.target.value as ReagentType)}
                className="w-full text-sm px-3 py-2 text-slate-100 focus:outline-none focus:border-sky-500 rb-inner-card-sm">
                {(Object.entries(REAGENT_LABELS) as [ReagentType, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            {params.reagent !== 'none' && (
              <div className="flex items-center justify-between px-3 py-2.5 rb-inner-card-sm">
                <span className="text-xs text-slate-400">Estimated dose ({params.reagent === 'NaOH' ? 'NaOH' : 'Ca(OH)₂'})</span>
                <span className="text-sm font-bold text-sky-300">{f(result.pHDose,0)} g/m³</span>
              </div>
            )}
          </div>
        </div>

        {/* ─ RIGHT: Results ─ */}
        <div className="space-y-5">

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <MetricCard label="Free NH₃ fraction"  value={f(result.fNH3*100,1)}     unit="%" sub={`pKa = ${f(result.pKa,3)}`} />
            <MetricCard label="Henry Hcc"           value={f(result.hcc,5)}           sub="Staudinger & Roberts" />
            <MetricCard label="H_eff"               value={f(result.hEff,5)}           sub="×f_NH₃" />
            <MetricCard label="Stripping factor S"  value={f(result.S,2)}
              warn={result.S < 1.5} accent={result.S >= 5}
              sub={result.S >= 5 ? 'Good' : result.S >= 1.5 ? 'Marginal' : 'Poor'} />
            <MetricCard label="NTU"                 value={f(result.NTU,2)}           sub="transfer units" warn={result.NTU > 20 || result.NTU < 2} />
            <MetricCard label="HTU"                 value={f(result.HTU,3)}           unit="m" sub={`${PACKING_HTU20[params.packingType].label.split(' ').slice(0,2).join(' ')}`} />
            <MetricCard label="Tower height Z"      value={f(result.Z,2)}             unit="m" sub="theoretical" />
            <MetricCard label="Design height"       value={f(result.Zdesign,2)}       unit="m" warn={result.Zdesign > 15} accent={result.Zdesign <= 10} sub={`×${params.safetyFactor} SF`} />
            <MetricCard label="Diameter D"          value={f(result.D,2)}             unit="m" sub={`A = ${f(result.A,2)} m²`} />
            <MetricCard label="Air flow Q_air"      value={f(result.Qair,0)}          unit="m³/h" sub={`G/L = ${params.glRatio}`} />
            <MetricCard label="Blower power"        value={f(result.blowerPower,2)}   unit="kW" sub={`ΔP = ${f(result.blowerPressure,2)} kPa`} />
            <MetricCard label="Effluent TAN"        value={f(result.tanOut,2)}        unit="mg/L"
              accent={result.tanOut <= params.targetTAN * 1.05} warn={result.tanOut > params.targetTAN * 1.2}
              sub={`target: ${params.targetTAN} mg/L`} />
            <MetricCard label="Removal efficiency"  value={f(result.removal,1)}       unit="%"
              accent={result.removal >= 90} warn={result.removal < 75} sub="TAN" />
            <MetricCard label="DO increase"         value={`+${f(result.doIncrease,2)}`} unit="mg/L" sub="re-aeration" />
          </div>

          <div className="p-5 rb-section">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200 mb-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Design Validation
              <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded ${
                failCount === 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
              }`}>{passCount}/{result.validations.length}</span>
            </h2>
            <div className="space-y-1.5">
              {result.validations.map((v, i) => (
                <div key={i} className={`flex items-center justify-between rounded-lg px-3 py-2 text-xs border ${
                  v.pass ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-rose-500/5 border-rose-500/20'
                }`}>
                  <div className="flex items-center gap-2">
                    {v.pass ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 text-rose-400 shrink-0" />}
                    <span className="text-slate-300">{v.label}</span>
                  </div>
                  <span className={`font-mono font-bold ${v.pass ? 'text-emerald-400' : 'text-rose-400'}`}>{v.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-5 rb-section">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200 mb-3">
              <Thermometer className="w-4 h-4 text-orange-400" /> Temperature Sensitivity
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    {['T (°C)', 'Hcc', 'S', 'NTU', 'HTU (m)', 'Z_design (m)', 'η (%)'].map(h => (
                      <th key={h} className="text-left py-2 px-2 text-slate-400 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[5, 10, 15, 20, 25, 30, 35, 40].map(t => {
                    const r = calcStripper({ ...params, temperature: t });
                    const isCurrent = t === params.temperature;
                    return (
                      <tr key={t} className={`border-b border-slate-800/60 ${isCurrent ? 'bg-sky-500/5' : 'hover:bg-slate-800/30'}`}>
                        <td className={`py-2 px-2 font-mono ${isCurrent ? 'text-sky-300 font-bold' : 'text-slate-300'}`}>{t}{isCurrent ? ' ←' : ''}</td>
                        <td className="py-2 px-2 font-mono text-slate-300">{f(r.hcc,4)}</td>
                        <td className={`py-2 px-2 font-mono ${r.S >= 5 ? 'text-emerald-400' : r.S >= 1.5 ? 'text-amber-400' : 'text-rose-400'}`}>{f(r.S,2)}</td>
                        <td className="py-2 px-2 font-mono text-slate-300">{f(r.NTU,2)}</td>
                        <td className="py-2 px-2 font-mono text-slate-300">{f(r.HTU,3)}</td>
                        <td className={`py-2 px-2 font-mono ${r.Zdesign <= 15 ? 'text-emerald-400' : 'text-rose-400'}`}>{f(r.Zdesign,2)}</td>
                        <td className={`py-2 px-2 font-mono ${r.removal >= 90 ? 'text-emerald-400' : r.removal >= 75 ? 'text-amber-400' : 'text-rose-400'}`}>{f(r.removal,1)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-5 rb-section">
              <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200 mb-4">
                <BarChart3 className="w-4 h-4 text-sky-400" /> Removal Efficiency vs pH
              </h2>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={phChartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="pH" tick={{ fontSize: 10, fill: '#94a3b8' }} label={{ value: 'pH', position: 'insideBottom', offset: -2, fill: '#94a3b8', fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} domain={[0, 100]} unit="%" />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 11 }} labelStyle={{ color: '#94a3b8' }} formatter={(val: number) => [`${val.toFixed(1)}%`, 'Removal']} />
                  <ReferenceLine x={params.pH} stroke="#0ea5e9" strokeDasharray="4 3" label={{ value: `pH ${params.pH}`, fill: '#0ea5e9', fontSize: 9 }} />
                  <ReferenceLine x={10} stroke="#f59e0b" strokeDasharray="4 3" label={{ value: 'pH 10', fill: '#f59e0b', fontSize: 9 }} />
                  <Line type="monotone" dataKey="removal" stroke="#0ea5e9" strokeWidth={2} dot={false} name="Removal %" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="p-5 rb-section">
              <h2 className="flex items-center gap-2 text-sm font-bold text-slate-200 mb-4">
                <Zap className="w-4 h-4 text-sky-400" /> Tower Height vs Temperature
              </h2>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={tempChartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="temp" tick={{ fontSize: 10, fill: '#94a3b8' }} label={{ value: 'T (°C)', position: 'insideBottom', offset: -2, fill: '#94a3b8', fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} unit=" m" />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 11 }} labelStyle={{ color: '#94a3b8' }} formatter={(val: number) => [`${val.toFixed(2)} m`, 'Design height']} />
                  <ReferenceLine x={params.temperature} stroke="#0ea5e9" strokeDasharray="4 3" label={{ value: `${params.temperature}°C`, fill: '#0ea5e9', fontSize: 9 }} />
                  <ReferenceLine y={15} stroke="#f43f5e" strokeDasharray="4 3" label={{ value: '15 m max', fill: '#f43f5e', fontSize: 9 }} />
                  <Line type="monotone" dataKey="Zdesign" stroke="#f97316" strokeWidth={2} dot={false} name="Z_design (m)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 px-4 py-3">
            <Info className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-300">
              <strong>DO Impact:</strong> Air stripping at G/L = {params.glRatio} re-aerates the effluent.
              Estimated DO increase: <strong>+{f(result.doIncrease,2)} mg/L</strong>. Beneficial for downstream biological treatment;
              may require post-pH correction before discharge (stripping raises effluent pH).
            </p>
          </div>

          <div className="overflow-hidden rb-section">
            <button onClick={() => setSciOpen(o => !o)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-rb-elevated transition-colors">
              <div className="flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-sky-400" />
                <span className="text-sm font-bold text-slate-200">Full Calculation Breakdown (Science)</span>
              </div>
              {sciOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {sciOpen && (
              <div className="px-5 pb-5 border-t border-slate-800">
                <ScienceAccordion sections={scienceSections} defaultOpen={false} />
              </div>
            )}
          </div>
        </div>
      </div>

      {showReport && <ReportModal params={params} result={result} onClose={() => setShowReport(false)} />}
    </div>
  );
}
